// ======================== LE CERCLE ALDRIN'S - PAGE FIDÉLITÉ CLIENT ========================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
    Award, Gift, Star, Coffee, Cake, Tag, Utensils, 
    Phone, User, ArrowRight, LogOut, History, 
    QrCode, Check, Crown, Sparkles, ChevronLeft, Home
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Configuration des couleurs
const COLORS = {
    primary: '#166534',      // Vert foncé
    primaryLight: '#22c55e', // Vert clair
    gold: '#FFD700',
    silver: '#C0C0C0',
    bronze: '#CD7F32',
    white: '#FFFFFF',
    gray: '#6B7280'
};

// Icône selon le type de récompense
const RewardIcon = ({ rewardId }) => {
    const icons = {
        'reward_10': Coffee,
        'reward_20': Cake,
        'reward_40': Tag,
        'reward_60': Utensils
    };
    const Icon = icons[rewardId] || Gift;
    return <Icon className="w-6 h-6" />;
};

// Icône de statut
const StatusIcon = ({ status }) => {
    if (status === 'Or') return <Crown className="w-5 h-5 text-yellow-500" />;
    if (status === 'Argent') return <Star className="w-5 h-5 text-gray-400" />;
    return <Award className="w-5 h-5 text-orange-600" />;
};

const ClientLoyaltyPage = () => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [showQRDialog, setShowQRDialog] = useState(false);
    const [currentQR, setCurrentQR] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    
    // Form states
    const [phone, setPhone] = useState('');
    const [firstName, setFirstName] = useState('');
    const [email, setEmail] = useState('');

    // Check if already logged in
    useEffect(() => {
        const savedMember = localStorage.getItem('loyalty_member');
        if (savedMember) {
            const memberData = JSON.parse(savedMember);
            refreshMemberData(memberData.id);
        }
    }, []);

    const refreshMemberData = async (memberId) => {
        try {
            const res = await fetch(`${API_URL}/api/loyalty/member/${memberId}`);
            if (res.ok) {
                const data = await res.json();
                setMember(data);
                setIsLoggedIn(true);
                localStorage.setItem('loyalty_member', JSON.stringify(data));
            } else {
                localStorage.removeItem('loyalty_member');
            }
        } catch (error) {
            console.error('Error refreshing member:', error);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!phone || phone.length < 8) {
            toast.error('Veuillez entrer un numéro de téléphone valide');
            return;
        }
        
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/loyalty/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone })
            });
            
            const data = await res.json();
            
            if (res.ok) {
                setMember(data.member);
                setIsLoggedIn(true);
                localStorage.setItem('loyalty_member', JSON.stringify(data.member));
                toast.success(`Bienvenue ${data.member.first_name} !`);
            } else {
                toast.error(data.detail || 'Numéro non trouvé');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!firstName || firstName.length < 2) {
            toast.error('Veuillez entrer votre prénom');
            return;
        }
        if (!phone || phone.length < 8) {
            toast.error('Veuillez entrer un numéro de téléphone valide');
            return;
        }
        
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/loyalty/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    first_name: firstName, 
                    phone,
                    email: email || null
                })
            });
            
            const data = await res.json();
            
            if (res.ok) {
                setMember(data.member);
                setIsLoggedIn(true);
                localStorage.setItem('loyalty_member', JSON.stringify(data.member));
                setShowRegister(false);
                toast.success(data.message);
            } else {
                toast.error(data.detail || 'Erreur lors de l\'inscription');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        setIsLoggedIn(false);
        setMember(null);
        localStorage.removeItem('loyalty_member');
        setPhone('');
        toast.success('À bientôt !');
    };

    const handleRedeemReward = async (rewardId) => {
        if (!member) return;
        
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/loyalty/generate-reward-qr`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    member_id: member.id,
                    reward_id: rewardId
                })
            });
            
            const data = await res.json();
            
            if (res.ok) {
                setCurrentQR({
                    code: data.qr_code,
                    qr_content: data.qr_code_content || `REWARD:${data.qr_code}`,
                    reward: data.reward,
                    points_cost: data.points_cost,
                    member_name: data.member_name,
                    expires_at: data.expires_at,
                    validity_minutes: data.validity_minutes || 5
                });
                setShowQRDialog(true);
                toast.success('QR code généré !', {
                    description: `Valide ${data.validity_minutes || 5} minutes`
                });
            } else {
                toast.error(data.detail || 'Erreur');
            }
        } catch (error) {
            toast.error('Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    // ======================== RENDER - NOT LOGGED IN ========================
    if (!isLoggedIn) {
        return (
            <div className="min-h-screen bg-white">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-800 to-green-600 text-white py-12 px-4 relative">
                    {/* Bouton Retour Accueil */}
                    <button
                        onClick={() => navigate('/client')}
                        className="absolute top-4 left-4 flex items-center gap-1 text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full px-3 py-2"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="text-sm font-medium">Accueil</span>
                    </button>
                    
                    <div className="max-w-md mx-auto text-center">
                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Sparkles className="w-10 h-10" />
                        </div>
                        <h1 className="text-3xl font-bold mb-2">Le Cercle Aldrin's</h1>
                        <p className="text-green-100">Programme de fidélité exclusif</p>
                    </div>
                </div>

                <div className="max-w-md mx-auto px-4 py-8">
                    {!showRegister ? (
                        <>
                            {/* Login Form */}
                            <Card className="border-0 shadow-lg">
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-green-800 text-xl">Accéder à mon compte</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleLogin} className="space-y-4">
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <Input
                                                type="tel"
                                                placeholder="Numéro de téléphone"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                className="pl-10 h-12 text-lg border-gray-200 focus:border-green-500"
                                            />
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full h-12 bg-green-700 hover:bg-green-800 text-lg font-semibold"
                                            disabled={loading}
                                        >
                                            {loading ? 'Connexion...' : 'Me connecter'}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>

                            {/* Register CTA */}
                            <div className="mt-8 text-center">
                                <p className="text-gray-600 mb-4">Pas encore membre ?</p>
                                <Button 
                                    onClick={() => setShowRegister(true)}
                                    variant="outline"
                                    className="border-green-700 text-green-700 hover:bg-green-50 h-12 px-8 text-lg"
                                >
                                    <Gift className="w-5 h-5 mr-2" />
                                    Rejoindre le Cercle Aldrin's
                                </Button>
                                <p className="text-sm text-gray-500 mt-3">3 points offerts à l'inscription !</p>
                            </div>

                            {/* Benefits */}
                            <div className="mt-10">
                                <h3 className="text-green-800 font-semibold text-center mb-4">Vos avantages</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-green-50 rounded-xl p-4 text-center">
                                        <Coffee className="w-8 h-8 text-green-700 mx-auto mb-2" />
                                        <p className="text-sm text-green-800">Café offert</p>
                                        <p className="text-xs text-gray-500">10 points</p>
                                    </div>
                                    <div className="bg-green-50 rounded-xl p-4 text-center">
                                        <Cake className="w-8 h-8 text-green-700 mx-auto mb-2" />
                                        <p className="text-sm text-green-800">Dessert offert</p>
                                        <p className="text-xs text-gray-500">20 points</p>
                                    </div>
                                    <div className="bg-green-50 rounded-xl p-4 text-center">
                                        <Tag className="w-8 h-8 text-green-700 mx-auto mb-2" />
                                        <p className="text-sm text-green-800">-2000 FCFA</p>
                                        <p className="text-xs text-gray-500">40 points</p>
                                    </div>
                                    <div className="bg-green-50 rounded-xl p-4 text-center">
                                        <Utensils className="w-8 h-8 text-green-700 mx-auto mb-2" />
                                        <p className="text-sm text-green-800">Plat offert</p>
                                        <p className="text-xs text-gray-500">60 points</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Register Form */}
                            <Card className="border-0 shadow-lg">
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-green-800 text-xl">Rejoindre le Cercle</CardTitle>
                                    <p className="text-sm text-gray-500">3 points offerts à l'inscription</p>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <Input
                                                type="text"
                                                placeholder="Prénom"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                className="pl-10 h-12 text-lg border-gray-200 focus:border-green-500"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                            <Input
                                                type="tel"
                                                placeholder="Numéro de téléphone"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                className="pl-10 h-12 text-lg border-gray-200 focus:border-green-500"
                                            />
                                        </div>
                                        <Input
                                            type="email"
                                            placeholder="Email (facultatif)"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="h-12 text-lg border-gray-200 focus:border-green-500"
                                        />
                                        <Button 
                                            type="submit" 
                                            className="w-full h-12 bg-green-700 hover:bg-green-800 text-lg font-semibold"
                                            disabled={loading}
                                        >
                                            {loading ? 'Inscription...' : 'Créer mon compte'}
                                        </Button>
                                    </form>
                                    <Button 
                                        variant="ghost" 
                                        className="w-full mt-4 text-gray-500"
                                        onClick={() => setShowRegister(false)}
                                    >
                                        J'ai déjà un compte
                                    </Button>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ======================== RENDER - LOGGED IN ========================
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header with status */}
            <div 
                className="text-white py-8 px-4 relative"
                style={{ 
                    background: member.status === 'Or' 
                        ? 'linear-gradient(135deg, #854d0e 0%, #ca8a04 100%)' 
                        : member.status === 'Argent'
                        ? 'linear-gradient(135deg, #374151 0%, #6b7280 100%)'
                        : 'linear-gradient(135deg, #166534 0%, #22c55e 100%)'
                }}
            >
                {/* Bouton Retour Accueil */}
                <button
                    onClick={() => navigate('/client')}
                    className="absolute top-4 left-4 flex items-center gap-1 text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full px-3 py-2"
                >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="text-sm font-medium">Accueil</span>
                </button>
                
                <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-4 mt-8">
                        <div className="flex items-center gap-2">
                            <StatusIcon status={member.status} />
                            <span className="font-medium">{member.status}</span>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-white/80 hover:text-white hover:bg-white/10"
                            onClick={handleLogout}
                        >
                            <LogOut className="w-4 h-4 mr-1" />
                            Déconnexion
                        </Button>
                    </div>
                    
                    <h1 className="text-2xl font-bold mb-1">Bonjour {member.first_name} !</h1>
                    <p className="text-white/80 text-sm">Bienvenue dans le Cercle Aldrin's</p>
                    
                    {/* Points Display */}
                    <div className="bg-white/20 rounded-2xl p-6 mt-6 backdrop-blur-sm">
                        <div className="text-center">
                            <p className="text-white/80 text-sm mb-1">Vos points</p>
                            <p className="text-5xl font-bold">{member.points}</p>
                            <p className="text-sm text-white/80 mt-1">points</p>
                        </div>
                        
                        {/* Progress to next reward */}
                        {member.next_reward && (
                            <div className="mt-4">
                                <div className="flex justify-between text-sm text-white/80 mb-2">
                                    <span>Prochaine récompense</span>
                                    <span>{member.next_reward.points_needed} pts restants</span>
                                </div>
                                <Progress 
                                    value={(member.points / member.next_reward.points_required) * 100}
                                    className="h-2 bg-white/20"
                                />
                                <p className="text-center text-sm mt-2 text-white/90">
                                    {member.next_reward.name}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-md mx-auto px-4 py-6">
                {/* Available Rewards */}
                {member.available_rewards && member.available_rewards.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-green-800 font-semibold mb-3 flex items-center gap-2">
                            <Gift className="w-5 h-5" />
                            Récompenses disponibles
                        </h2>
                        <div className="space-y-3">
                            {member.available_rewards.map((reward) => (
                                <Card key={reward.id} className="border-green-200 bg-green-50">
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-700">
                                                    <RewardIcon rewardId={reward.id} />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-green-800">{reward.name}</p>
                                                    <p className="text-sm text-green-600">{reward.points_required} points</p>
                                                </div>
                                            </div>
                                            <Button 
                                                size="sm"
                                                className="bg-green-700 hover:bg-green-800"
                                                onClick={() => handleRedeemReward(reward.id)}
                                                disabled={loading}
                                            >
                                                <QrCode className="w-4 h-4 mr-1" />
                                                Utiliser
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* All Rewards */}
                <div className="mb-6">
                    <h2 className="text-green-800 font-semibold mb-3 flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Toutes les récompenses
                    </h2>
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        {[
                            { id: 'reward_10', points: 10, name: 'Café ou jus naturel offert', icon: Coffee },
                            { id: 'reward_20', points: 20, name: 'Dessert offert', icon: Cake },
                            { id: 'reward_40', points: 40, name: 'Réduction 2000 FCFA', icon: Tag },
                            { id: 'reward_60', points: 60, name: 'Plat du jour offert', icon: Utensils }
                        ].map((reward, index) => {
                            const isAvailable = member.points >= reward.points;
                            const Icon = reward.icon;
                            return (
                                <div 
                                    key={reward.id}
                                    className={`flex items-center gap-3 p-4 ${index > 0 ? 'border-t' : ''} ${isAvailable ? 'bg-green-50' : ''}`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAvailable ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <p className={`font-medium ${isAvailable ? 'text-green-800' : 'text-gray-600'}`}>{reward.name}</p>
                                        <p className={`text-sm ${isAvailable ? 'text-green-600' : 'text-gray-400'}`}>{reward.points} points</p>
                                    </div>
                                    {isAvailable && (
                                        <Check className="w-5 h-5 text-green-600" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Status Benefits */}
                {member.status_benefits && member.status_benefits.length > 0 && (
                    <div className="mb-6">
                        <h2 className="text-green-800 font-semibold mb-3 flex items-center gap-2">
                            <Crown className="w-5 h-5" />
                            Avantages {member.status}
                        </h2>
                        <Card className="bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200">
                            <CardContent className="p-4">
                                <ul className="space-y-2">
                                    {member.status_benefits.map((benefit, index) => (
                                        <li key={index} className="flex items-center gap-2 text-amber-800">
                                            <Check className="w-4 h-4 text-amber-600" />
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* History Button */}
                <Button 
                    variant="outline" 
                    className="w-full border-green-200 text-green-700"
                    onClick={() => setShowHistory(true)}
                >
                    <History className="w-4 h-4 mr-2" />
                    Voir mon historique
                </Button>

                {/* Info */}
                <p className="text-center text-sm text-gray-500 mt-6">
                    1000 FCFA dépensés = 1 point
                </p>
            </div>

            {/* QR Code Dialog */}
            <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
                <DialogContent className="max-w-sm mx-auto">
                    <DialogHeader>
                        <DialogTitle className="text-center text-green-800 flex items-center justify-center gap-2">
                            <Gift className="w-5 h-5" />
                            Votre récompense
                        </DialogTitle>
                        <DialogDescription className="text-center">
                            Présentez ce QR code au serveur
                        </DialogDescription>
                    </DialogHeader>
                    {currentQR && (
                        <div className="flex flex-col items-center py-4">
                            {/* QR Code */}
                            <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-green-200">
                                <QRCode 
                                    value={currentQR.qr_content || `REWARD:${currentQR.code}`} 
                                    size={200} 
                                    level="H"
                                />
                            </div>
                            
                            {/* Token visible */}
                            <p className="mt-3 text-lg font-mono font-bold text-green-800 bg-green-50 px-4 py-2 rounded-lg">
                                {currentQR.code}
                            </p>
                            
                            {/* Reward info */}
                            <div className="mt-4 text-center w-full">
                                <p className="text-lg font-semibold text-gray-800">
                                    {currentQR.reward?.name}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                    Coût: <span className="font-bold text-purple-600">-{currentQR.points_cost || currentQR.reward?.points_required} points</span>
                                </p>
                            </div>
                            
                            {/* Expiration warning */}
                            <div className="mt-4 w-full p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-center justify-center gap-2 text-amber-700">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="font-medium">
                                        Valide {currentQR.validity_minutes || 5} minutes
                                    </span>
                                </div>
                                <p className="text-xs text-amber-600 text-center mt-1">
                                    Présentez rapidement au serveur avant expiration
                                </p>
                            </div>
                            
                            {/* Instructions */}
                            <div className="mt-4 w-full p-3 bg-green-50 rounded-lg">
                                <p className="text-sm text-green-700 text-center">
                                    Le serveur scannera ce QR code en caisse pour valider votre récompense
                                </p>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* History Dialog */}
            <Dialog open={showHistory} onOpenChange={setShowHistory}>
                <DialogContent className="max-w-sm mx-auto max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-green-800">Historique des points</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-4">
                        {member.points_history && member.points_history.slice().reverse().map((entry, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div>
                                    <p className="font-medium text-sm">{entry.description}</p>
                                    <p className="text-xs text-gray-500">
                                        {new Date(entry.date).toLocaleDateString('fr-FR')}
                                    </p>
                                </div>
                                <span className={`font-bold ${entry.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {entry.points > 0 ? '+' : ''}{entry.points}
                                </span>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ClientLoyaltyPage;
